// frontend/src/ProductionLoss.js

import React, { useEffect, useMemo, useState } from "react";
import api from "./api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import { Edit, Trash2, Save, X, Eye, EyeOff } from "lucide-react";

const LINES = ["A Line", "B Line", "C Line", "D Line"];

const emptyForm = {
  line: "A Line",
  date_from: "",
  date_to: "",
  time_loss: "",
  product_loss: "",
};

export default function ProductionLoss() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [showRecords, setShowRecords] = useState(false);

  const [filters, setFilters] = useState({
    line: "All",
    date_from: "",
    date_to: "",
  });

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/production-loss");
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching production loss", err);
      alert("Could not load Production Loss data.");
    } finally {
      setLoading(false);
    }
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    if (!year || !month || !day) return dateString;
    return `${month}-${day}-${year.slice(-2)}`;
  };

  const formatDateLabel = (from, to) => {
    const f = formatShortDate(from);
    const t = formatShortDate(to);

    if (!f && !t) return "No date";
    if (f === t) return f;
    return `${f}→${t}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.date_from || !form.date_to) {
      alert("Please enter Date From and Date To.");
      return;
    }

    try {
      await api.post("/api/production-loss", {
        ...form,
        time_loss: Number(form.time_loss || 0),
        product_loss: Number(form.product_loss || 0),
      });

      setForm(emptyForm);
      fetchRows();
    } catch (err) {
      console.error("Error saving production loss", err);
      alert(err?.response?.data?.error || "Could not save data.");
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      line: row.line || "A Line",
      date_from: row.date_from || "",
      date_to: row.date_to || "",
      time_loss: row.time_loss ?? "",
      product_loss: row.product_loss ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyForm);
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/api/production-loss/${id}`, {
        ...editForm,
        time_loss: Number(editForm.time_loss || 0),
        product_loss: Number(editForm.product_loss || 0),
      });

      cancelEdit();
      fetchRows();
    } catch (err) {
      console.error("Error updating production loss", err);
      alert(err?.response?.data?.error || "Could not update data.");
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this production loss record?")) return;

    try {
      await api.delete(`/api/production-loss/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Error deleting production loss", err);
      alert("Could not delete data.");
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filters.line !== "All" && r.line !== filters.line) return false;
      if (filters.date_from && r.date_from < filters.date_from) return false;
      if (filters.date_to && r.date_to > filters.date_to) return false;
      return true;
    });
  }, [rows, filters]);

  const chartDataByLine = useMemo(() => {
    const grouped = {};

    LINES.forEach((line) => {
      grouped[line] = rows
        .filter((r) => r.line === line)
        .slice()
        .sort((a, b) => {
          const da = new Date(a.date_from || a.created_at || 0).getTime();
          const db = new Date(b.date_from || b.created_at || 0).getTime();
          return da - db;
        })
        .map((r) => ({
          label: formatDateLabel(r.date_from, r.date_to),
          time_loss: Number(r.time_loss || 0),
          product_loss: Number(r.product_loss || 0),
        }));
    });

    return grouped;
  }, [rows]);

  const ChartCard = ({ line }) => {
    const data = chartDataByLine[line] || [];

    return (
      <div className="card">
        <h3 style={{ marginTop: 0 }}>{line}</h3>
        <p style={{ marginTop: -4, fontSize: 12, color: "#9ca3af" }}>
          Evolution of time loss and product loss. Fixed scale: 0 to 30,000.
        </p>

        <div style={{ height: 300 }}>
          {data.length === 0 ? (
            <p style={{ color: "#9ca3af" }}>No data for {line} yet.</p>
          ) : (
            <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
              <div style={{ width: Math.max(data.length * 52, 240), height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data}
                    barCategoryGap="0%"
                    barGap={0}
                    margin={{ top: 10, right: 10, left: 0, bottom: 35 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />

                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 9 }}
                      angle={-15}
                      textAnchor="end"
                      interval={0}
                      height={45}
                    />

                    <YAxis
                      domain={[0, 30000]}
                      ticks={[0, 5000, 10000, 15000, 20000, 25000, 30000]}
                      allowDataOverflow={true}
                    />

                    <Tooltip />
                    <Legend />

                    <Bar
                      dataKey="time_loss"
                      name="Time Loss"
                      fill="#38bdf8"
                      barSize={12}
                      radius={[5, 5, 0, 0]}
                    />

                    <Bar
                      dataKey="product_loss"
                      name="Product Loss"
                      fill="#f97316"
                      barSize={12}
                      radius={[5, 5, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="main-content">
      <h1>📉 Production Loss</h1>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Add Production Loss Data</h3>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr 1fr 1fr 1fr auto",
            gap: 12,
            alignItems: "end",
          }}
        >
          <div>
            <label>Line</label>
            <select name="line" value={form.line} onChange={handleChange}>
              {LINES.map((line) => (
                <option key={line} value={line}>
                  {line}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Date From</label>
            <input
              type="date"
              name="date_from"
              value={form.date_from}
              onChange={handleChange}
            />
          </div>

          <div>
            <label>Date To</label>
            <input
              type="date"
              name="date_to"
              value={form.date_to}
              onChange={handleChange}
            />
          </div>

          <div>
            <label>Time Loss</label>
            <input
              type="number"
              step="0.01"
              name="time_loss"
              value={form.time_loss}
              onChange={handleChange}
            />
          </div>

          <div>
            <label>Product Loss</label>
            <input
              type="number"
              step="0.01"
              name="product_loss"
              value={form.product_loss}
              onChange={handleChange}
            />
          </div>

          <button type="submit" className="btn-primary">
            Save
          </button>
        </form>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {LINES.map((line) => (
          <ChartCard key={line} line={line} />
        ))}
      </div>

      <div className="card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>
              Saved Production Loss Records
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>
              Hidden by default. Open this only when you need to search, edit, or
              delete data.
            </p>
          </div>

          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowRecords((p) => !p)}
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            {showRecords ? <EyeOff size={16} /> : <Eye size={16} />}
            {showRecords ? "Hide Records" : "Show Records / Edit Data"}
          </button>
        </div>

        {showRecords && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr auto",
                gap: 12,
                marginTop: 16,
                marginBottom: 16,
                alignItems: "end",
              }}
            >
              <div>
                <label>Filter by Line</label>
                <select
                  value={filters.line}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, line: e.target.value }))
                  }
                >
                  <option value="All">All Lines</option>
                  {LINES.map((line) => (
                    <option key={line} value={line}>
                      {line}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label>Date From</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, date_from: e.target.value }))
                  }
                />
              </div>

              <div>
                <label>Date To</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) =>
                    setFilters((p) => ({ ...p, date_to: e.target.value }))
                  }
                />
              </div>

              <button
                type="button"
                className="btn-sm btn-purple"
                onClick={() =>
                  setFilters({ line: "All", date_from: "", date_to: "" })
                }
              >
                Clear Filters
              </button>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : filteredRows.length === 0 ? (
              <p style={{ color: "#9ca3af" }}>No records match your filters.</p>
            ) : (
              <div className="table-wrapper">
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Date From</th>
                        <th>Date To</th>
                        <th>Time Loss</th>
                        <th>Product Loss</th>
                        <th style={{ width: 120 }}>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredRows.map((row) => {
                        const isEditing = editingId === row.id;

                        return (
                          <tr key={row.id}>
                            <td>
                              {isEditing ? (
                                <select
                                  value={editForm.line}
                                  onChange={(e) =>
                                    setEditForm((p) => ({
                                      ...p,
                                      line: e.target.value,
                                    }))
                                  }
                                >
                                  {LINES.map((line) => (
                                    <option key={line} value={line}>
                                      {line}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                row.line
                              )}
                            </td>

                            <td>
                              {isEditing ? (
                                <input
                                  type="date"
                                  value={editForm.date_from}
                                  onChange={(e) =>
                                    setEditForm((p) => ({
                                      ...p,
                                      date_from: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                row.date_from
                              )}
                            </td>

                            <td>
                              {isEditing ? (
                                <input
                                  type="date"
                                  value={editForm.date_to}
                                  onChange={(e) =>
                                    setEditForm((p) => ({
                                      ...p,
                                      date_to: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                row.date_to
                              )}
                            </td>

                            <td>
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editForm.time_loss}
                                  onChange={(e) =>
                                    setEditForm((p) => ({
                                      ...p,
                                      time_loss: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                row.time_loss
                              )}
                            </td>

                            <td>
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editForm.product_loss}
                                  onChange={(e) =>
                                    setEditForm((p) => ({
                                      ...p,
                                      product_loss: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                row.product_loss
                              )}
                            </td>

                            <td>
                              <div style={{ display: "flex", gap: 8 }}>
                                {isEditing ? (
                                  <>
                                    <button
                                      type="button"
                                      className="icon-btn"
                                      onClick={() => saveEdit(row.id)}
                                      title="Save"
                                    >
                                      <Save size={16} />
                                    </button>

                                    <button
                                      type="button"
                                      className="icon-btn ghost"
                                      onClick={cancelEdit}
                                      title="Cancel"
                                    >
                                      <X size={16} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className="icon-btn"
                                      onClick={() => startEdit(row)}
                                      title="Edit"
                                    >
                                      <Edit size={16} />
                                    </button>

                                    <button
                                      type="button"
                                      className="icon-btn danger"
                                      onClick={() => deleteRow(row.id)}
                                      title="Delete"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}